import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { MailCheck, RefreshCw, X } from "lucide-react";
import { API, useApp } from "../context";

// App-wide email-confirmation nudge + soft lockout.
//  - Unverified & within the grace window  -> slim persistent top banner (dismissible per session).
//  - Unverified & past the grace window     -> full-screen soft lock; the app is blocked until they confirm.
// Verified users see nothing. Grace length is admin-configurable (default 7 days).
export default function EmailConfirmGate() {
  const { user } = useApp();
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [preview, setPreview] = useState(false);

  // Admin "preview the lockout screen" entry: /?previewLockout=1
  useEffect(() => {
    try {
      setPreview(new URLSearchParams(window.location.search).get("previewLockout") === "1");
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(() => {
    if (!user || user.email_verified) { setStatus(null); return; }
    axios.get(`${API}/auth/verification-status`).then((r) => setStatus(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const resend = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/auth/send-verification`, {});
      setSent(true);
    } catch { /* ignore */ } finally { setSending(false); }
  };

  const closePreview = () => {
    setPreview(false);
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete("previewLockout");
      window.history.replaceState({}, "", u.toString());
    } catch { /* ignore */ }
  };

  // ---- Admin preview of the lockout screen (works regardless of verified state) ----
  if (preview) {
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" data-testid="email-confirm-lockout-preview"
        style={{ background: "rgba(6,10,20,0.92)", backdropFilter: "blur(6px)" }}>
        <div className="w-full max-w-md rounded-3xl border-2 border-amber-400/40 p-7 text-center"
          style={{ background: "linear-gradient(180deg, rgba(30,20,6,0.98), rgba(20,16,8,0.98))", boxShadow: "0 0 80px rgba(245,158,11,0.25)" }}>
          <div className="inline-block mb-3 px-2 py-0.5 rounded-full bg-white/10 text-white/60 text-[10px] font-black uppercase tracking-wider">Admin preview</div>
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center">
            <MailCheck className="text-amber-300" size={34} />
          </div>
          <h2 className="text-white font-black text-2xl mb-2">Please confirm your email to keep exploring</h2>
          <p className="text-white/70 text-sm mb-5 leading-relaxed">
            This is what an unconfirmed user sees once the grace period passes. The app is blocked until they confirm.
          </p>
          <button disabled className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-black text-amber-950 opacity-90"
            style={{ background: "linear-gradient(135deg,#F59E0B,#FCD34D)" }}>
            <RefreshCw size={16} /> Resend the confirmation email
          </button>
          <button onClick={closePreview} className="mt-3 text-white/60 hover:text-white text-xs font-bold" data-testid="lockout-preview-close">
            Close preview
          </button>
        </div>
      </div>
    );
  }

  if (!user || user.email_verified) return null;
  if (!status || status.email_verified) return null;

  // ---- Soft lockout (grace period elapsed) ----
  if (status.locked) {
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" data-testid="email-confirm-lockout"
        style={{ background: "rgba(6,10,20,0.92)", backdropFilter: "blur(6px)" }}>
        <div className="w-full max-w-md rounded-3xl border-2 border-amber-400/40 p-7 text-center"
          style={{ background: "linear-gradient(180deg, rgba(30,20,6,0.98), rgba(20,16,8,0.98))", boxShadow: "0 0 80px rgba(245,158,11,0.25)" }}>
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center">
            <MailCheck className="text-amber-300" size={34} />
          </div>
          <h2 className="text-white font-black text-2xl mb-2">Please confirm your email to keep exploring</h2>
          <p className="text-white/70 text-sm mb-5 leading-relaxed">
            Your account was created but the email <strong className="text-amber-200">{status.email}</strong> is still unconfirmed.
            To keep Isaiah&rsquo;s World safe, confirming is now required before you can continue. Tap the button in the email we sent,
            then refresh this page.
          </p>
          {sent ? (
            <div className="rounded-xl bg-emerald-500/15 border border-emerald-400/40 px-4 py-3 text-emerald-200 text-sm font-bold mb-4" data-testid="lockout-resent">
              Sent! Check your inbox (and spam) for the confirm link.
            </div>
          ) : null}
          <button onClick={resend} disabled={sending} data-testid="lockout-resend-btn"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-black text-amber-950 disabled:opacity-60"
            style={{ background: "linear-gradient(135deg,#F59E0B,#FCD34D)" }}>
            {sending ? "Sending..." : <><RefreshCw size={16} /> Resend the confirmation email</>}
          </button>
          <button onClick={load} className="mt-3 text-white/50 hover:text-white text-xs font-bold" data-testid="lockout-refresh-btn">
            I&rsquo;ve confirmed &mdash; refresh
          </button>
        </div>
      </div>
    );
  }

  // ---- Within grace: persistent (dismissible) nudge banner ----
  if (dismissed) return null;
  return (
    <div className="w-full px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid="email-confirm-banner"
      style={{ background: "linear-gradient(90deg, rgba(245,158,11,0.18), rgba(16,185,129,0.14))", borderBottom: "1px solid rgba(245,158,11,0.3)" }}>
      <MailCheck className="text-amber-300 shrink-0" size={17} />
      <span className="text-white text-sm font-semibold flex-1 min-w-[200px]">
        Confirm your email to secure your account
        {status.hours_left != null && status.hours_left <= 72 ? (
          <span className="text-amber-200"> &mdash; {status.hours_left}h left before access is limited</span>
        ) : null}
      </span>
      {sent ? (
        <span className="text-emerald-200 text-xs font-bold" data-testid="banner-resent">Confirmation sent, check your inbox</span>
      ) : (
        <button onClick={resend} disabled={sending} data-testid="confirm-resend-btn"
          className="px-3 py-1.5 rounded-full bg-amber-500 text-amber-950 text-xs font-black disabled:opacity-60">
          {sending ? "Sending..." : "Resend email"}
        </button>
      )}
      <button onClick={() => setDismissed(true)} data-testid="confirm-banner-dismiss" className="text-white/40 hover:text-white shrink-0">
        <X size={16} />
      </button>
    </div>
  );
}
